import React, { useEffect, useRef, useState, useCallback } from 'react';
import './ImageAnnotateModal.css';

// Built-in image editor (operator 2026-10-20): DRAW ON THE IMAGE, nothing else —
// no crop, no resize. Freehand pen in a few colours and widths, undo, clear,
// save. Works with a mouse, a finger or a stylus (pointer events; the canvas
// has touch-action:none so a finger draws instead of scrolling). The drawing
// is done at the image's own pixel size (the canvas is only SCALED on screen)
// so the saved picture keeps its resolution; the strokes are kept as a list so
// Undo is exact and the picture is redrawn from the original each time.
//
// `source` is a File/Blob (local box) or a URL string (an already-uploaded
// image — fetched with the session cookie so the canvas is not tainted).
// onSave(file) hands back a NEW File (JPEG, or PNG when the original was PNG)
// named "<original>-marked.<ext>"; the caller runs its usual compress/upload.
const COLOURS = [
  { name: 'Red', value: '#e53935' },
  { name: 'Yellow', value: '#fdd835' },
  { name: 'Blue', value: '#1e88e5' },
  { name: 'Black', value: '#111111' },
  { name: 'White', value: '#ffffff' },
];
const WIDTHS = [
  { name: 'Thin', factor: 0.004 },
  { name: 'Medium', factor: 0.009 },
  { name: 'Thick', factor: 0.018 },
];

const baseName = (name) => String(name || 'image').replace(/\.[^.]+$/, '');

export default function ImageAnnotateModal({ isOpen, source, fileName, onSave, onCancel }) {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);          // the decoded original (HTMLImageElement)
  const strokesRef = useRef([]);          // [{ colour, width, points: [[x,y],…] }] in canvas pixels
  const activeRef = useRef(null);         // stroke being drawn
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [colour, setColour] = useState(COLOURS[0].value);
  const [widthIdx, setWidthIdx] = useState(1);
  const [strokeCount, setStrokeCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [mime, setMime] = useState('image/jpeg');

  // Redraw: original picture, then every stroke, then the one in progress.
  const redraw = useCallback(() => {
    const canvas = canvasRef.current; const img = imageRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const all = activeRef.current ? [...strokesRef.current, activeRef.current] : strokesRef.current;
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    for (const s of all) {
      if (!s.points.length) continue;
      ctx.strokeStyle = s.colour; ctx.lineWidth = s.width;
      ctx.beginPath();
      const [x0, y0] = s.points[0];
      ctx.moveTo(x0, y0);
      if (s.points.length === 1) ctx.lineTo(x0 + 0.01, y0 + 0.01);   // a dot
      for (let i = 1; i < s.points.length; i++) ctx.lineTo(s.points[i][0], s.points[i][1]);
      ctx.stroke();
    }
  }, []);

  // Load the source when opened.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false; let objectUrl = null;
    setReady(false); setError(''); strokesRef.current = []; activeRef.current = null; setStrokeCount(0);
    (async () => {
      try {
        let blob = source;
        if (typeof source === 'string') {
          const res = await fetch(source, { credentials: 'include' });
          if (!res.ok) throw new Error(`Could not load the image (${res.status})`);
          blob = await res.blob();
        }
        if (!blob || !(blob instanceof Blob)) throw new Error('No image to edit');
        setMime(blob.type === 'image/png' ? 'image/png' : 'image/jpeg');
        objectUrl = URL.createObjectURL(blob);
        const img = new Image();
        await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = () => reject(new Error('Could not decode the image')); img.src = objectUrl; });
        if (cancelled) return;
        imageRef.current = img;
        const canvas = canvasRef.current;
        // Draw at the picture's own size, capped at 2560 px on the long edge
        // (phones choke on 12-MP canvases; the upload compressor caps there anyway).
        const scale = Math.min(1, 2560 / Math.max(img.naturalWidth, img.naturalHeight));
        canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
        setReady(true);
        redraw();
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not open the image');
      }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [isOpen, source, redraw]);

  if (!isOpen) return null;

  const penWidth = () => {
    const c = canvasRef.current;
    const longEdge = c ? Math.max(c.width, c.height) : 1000;
    return Math.max(2, Math.round(longEdge * WIDTHS[widthIdx].factor));
  };
  const pointFromEvent = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * c.width, ((e.clientY - r.top) / r.height) * c.height];
  };
  const onPointerDown = (e) => {
    if (!ready || saving) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    activeRef.current = { colour, width: penWidth(), points: [pointFromEvent(e)] };
    redraw();
  };
  const onPointerMove = (e) => {
    if (!activeRef.current) return;
    e.preventDefault();
    activeRef.current.points.push(pointFromEvent(e));
    redraw();
  };
  const onPointerUp = (e) => {
    if (!activeRef.current) return;
    e.preventDefault();
    strokesRef.current = [...strokesRef.current, activeRef.current];
    activeRef.current = null;
    setStrokeCount(strokesRef.current.length);
    redraw();
  };
  const undo = () => { strokesRef.current = strokesRef.current.slice(0, -1); setStrokeCount(strokesRef.current.length); redraw(); };
  const clearAll = () => { strokesRef.current = []; setStrokeCount(0); redraw(); };

  const save = () => {
    const canvas = canvasRef.current;
    if (!canvas || !ready) return;
    setSaving(true);
    activeRef.current = null; redraw();
    const ext = mime === 'image/png' ? 'png' : 'jpg';
    canvas.toBlob((blob) => {
      setSaving(false);
      if (!blob) { setError('Could not save the drawing'); return; }
      onSave(new File([blob], `${baseName(fileName)}-marked.${ext}`, { type: mime, lastModified: Date.now() }));
    }, mime, 0.92);
  };

  return (
    <div className="modal-overlay annotate-overlay" onClick={() => { if (!saving) onCancel(); }}>
      <div className="modal-content annotate-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Draw on image</h3>
          <button type="button" className="close-button" onClick={onCancel} disabled={saving} aria-label="Close">&times;</button>
        </div>
        <div className="annotate-toolbar" role="toolbar" aria-label="Drawing tools">
          <div className="annotate-group" aria-label="Colour">
            {COLOURS.map((c) => (
              <button key={c.value} type="button" title={c.name} aria-label={c.name} aria-pressed={colour === c.value}
                className={`annotate-swatch ${colour === c.value ? 'is-active' : ''}`} style={{ background: c.value }}
                onClick={() => setColour(c.value)} />
            ))}
          </div>
          <div className="annotate-group" aria-label="Pen size">
            {WIDTHS.map((w, i) => (
              <button key={w.name} type="button" className={`btn btn-outline btn-small ${widthIdx === i ? 'is-active' : ''}`}
                aria-pressed={widthIdx === i} onClick={() => setWidthIdx(i)}>{w.name}</button>
            ))}
          </div>
          <div className="annotate-group">
            <button type="button" className="btn btn-outline btn-small" onClick={undo} disabled={!strokeCount || saving}>↶ Undo</button>
            <button type="button" className="btn btn-outline btn-small" onClick={clearAll} disabled={!strokeCount || saving}>Clear</button>
          </div>
        </div>
        <div className="annotate-stage">
          {!ready && !error && <div className="annotate-status">Loading image…</div>}
          {error && <div className="annotate-status annotate-error">{error}</div>}
          <canvas
            ref={canvasRef}
            className="annotate-canvas"
            style={{ display: ready ? 'block' : 'none' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onPointerLeave={onPointerUp}
          />
        </div>
        <div className="annotate-actions">
          <small className="annotate-hint">Draw with your finger, a stylus or the mouse. The picture itself is not cropped or resized.</small>
          <div>
            <button type="button" className="btn btn-outline" onClick={onCancel} disabled={saving}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={!ready || saving}>{saving ? 'Saving…' : (strokeCount ? 'Save drawing' : 'Save')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
